<template>
	<div id="search-box" class="navbar-box" :style="{ background: bgcolor }">
		<div class="back-wrap" @click="goback">
			<van-icon name="arrow-left" size="1.5rem" color="#000000" />
		</div>
		<div class="searchInput" @focusout="onBlur">
			<SearchInput
				v-model="searchVal"
				:placeholder="placeholderVal"
				:show-search="true"
				@search="searchBtn"
			/>
			<div v-if="showSuggestions && suggestions.length > 0" class="search-suggestions">
				<div v-for="(item, index) in suggestions" :key="index" class="suggestion-item" @mousedown.prevent="selectSuggestion(item)">
					{{ item.suggestion }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import SearchInput from '@/components/core/SearchInput.vue';
import { searchSuggestions } from '@/api/product.js';
import { debounce } from 'lodash-es';

const props = defineProps({
	backIndex: {
		type: Boolean,
		default: false,
	},
	placeholderValS: {
		type: String,
		default: '',
	},
	searchVals: {
		type: String,
		default: '',
	},
	bgcolor: {
		type: String,
		default: '',
	},
	focus: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['searchBtn']);

const router = useRouter();
const searchVal = ref(props.searchVals);
const placeholderVal = ref(props.placeholderValS);
const defaultFocus = ref(props.focus);
const suggestions = ref([]);
const showSuggestions = ref(false);
let stopInterval = null;

const handleInput = debounce(async val => {
	if (!val?.trim()) {
		suggestions.value = [];
		showSuggestions.value = false;
		return;
	}
	try {
		const res = await searchSuggestions({ query: val, hit: 20 });
		if (res?.suggestions) {
			suggestions.value = res.suggestions;
			showSuggestions.value = true;
		}
	} catch {
		suggestions.value = [];
	}
}, 500);

const onBlur = () => {
	if (import.meta.client) {
		window.scrollTo({ top: 0, behavior: 'auto' });
	}
	setTimeout(() => {
		defaultFocus.value = false;
		showSuggestions.value = false;
	}, 150);
};

const goback = () => {
	onBlur();
	setTimeout(() => {
		router.back();
	}, 100);
};

const searchBtn = () => {
	showSuggestions.value = false;
	emit('searchBtn', searchVal.value, { searchType: 1 });
};

const selectSuggestion = item => {
	searchVal.value = item.suggestion;
	showSuggestions.value = false;
	emit('searchBtn', searchVal.value, { searchType: 2 });
};

const stop = () => {
	if (!import.meta.client) return;
	stopInterval = setInterval(() => {
		document.activeElement?.blur?.();
	}, 20);
	setTimeout(() => {
		if (stopInterval) clearInterval(stopInterval);
	}, 500);
};

watch(
	() => props.placeholderValS,
	val => {
		placeholderVal.value = val;
	}
);
watch(
	() => props.searchVals,
	val => {
		searchVal.value = val;
	}
);
watch(searchVal, val => {
	handleInput(val);
});
watch(
	() => props.focus,
	val => {
		defaultFocus.value = val;
	}
);

onMounted(() => {
	if (props.focus && import.meta.client) {
		setTimeout(() => {
			const input = document.querySelector('.searchInput .search-input');
			input?.focus?.();
		}, 100);
	}
});

onBeforeUnmount(() => {
	onBlur();
	stop();
});
</script>

<style lang="scss" scoped>
.navbar-box {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.5rem 0.92rem;
}

.back-wrap {
	cursor: pointer;
	flex-shrink: 0;
	display: flex;
	align-items: center;
}

.searchInput {
	font-size: 1rem;
	flex: 1;
	margin-left: 12px;
	position: relative;
}

.search-suggestions {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	margin-top: 4px;
	background: #fff;
	z-index: 999;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	border-radius: 4px;
	max-height: 300px;
	overflow-y: auto;
}

.suggestion-item {
	padding: 10px 12px;
	border-bottom: 1px solid #eee;
	font-size: 14px;
	color: #333;
	cursor: pointer;

	&:last-child {
		border-bottom: none;
	}
}
</style>
